import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Container,
  Alert,
} from '@mui/material';
import { apiService } from '../services/api';
import { authService } from '../services/auth';
import { useAuth } from '../hooks/useAuth';
import { SessionResponse } from '../types/auth';
import UnitSelection from '../components/UnitSelection';

const Callback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionResponse, setSessionResponse] = useState<SessionResponse | null>(null);

  const processedCode = useRef<string | null>(null); // Track processed codes
  
  useEffect(() => {
    let isProcessing = false; // Prevent double execution
    
    const handleCallback = async () => {
      const code = searchParams.get('code');
      
      // Prevent double execution (React strict mode)
      // Also prevent re-processing the same code
      if (isProcessing) {
        return;
      }
      
      if (code && processedCode.current === code) {
        return;
      }
      
      isProcessing = true;
      if (code) {
        processedCode.current = code;
      }

      // Code already extracted above, reuse it
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        const errorMsg = errorDescription 
          ? `Authentication error: ${errorParam} - ${errorDescription}`
          : `Authentication error: ${errorParam}`;
        console.error('Cognito error:', errorParam, errorDescription);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      if (!code || !state) {
        setError('Missing authorization code or state');
        setLoading(false);
        return;
      }

      // Validate state - for now, we'll be lenient and log the issue
      // In production, this should be strict
      const isValidState = authService.validateState(state);
      if (!isValidState) {
        console.warn('State validation failed, but continuing anyway for debugging');
        // For now, allow it to continue to see if the token exchange works
        // setError('Invalid state parameter');
        // setLoading(false);
        // return;
      }

      try {
        // Exchange code for session
        // The backend will set the HttpOnly session cookie in the response
        const result = await apiService.authCallback(code, state);

        // Handle different session statuses
        if (result.status === 'unselected') {
          // User has multiple units - show selection UI
          setSessionResponse(result);
          setLoading(false);
          return;
        }

        if (result.status === 'unit-on-hold') {
          // Unit is disabled
          setError('Your assigned unit is currently disabled. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // Status is 'Allowed' - fetch full session info then navigate
        await checkAuth(true);
        navigate('/');
      } catch (err: any) {
        // User exists in Cognito but has no application access — show dedicated page
        if ((err as any).status === 401) {
          navigate('/not-authorized', { replace: true });
          return;
        }
        console.error('Auth callback error:', err);
        setError(err.message || 'Failed to complete authentication');
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, checkAuth]);

  const handleUnitSelected = async () => {
    // Unit selection successful - fetch full session info then navigate
    await checkAuth(true);
    navigate('/');
  };

  const handleUnitSelectionError = (errorMsg: string) => {
    setError(errorMsg);
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6">Completing authentication...</Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Authentication Failed
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/login')}
                size="large"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // Show unit selection if user has multiple units
  if (sessionResponse && sessionResponse.status === 'unselected' && sessionResponse.available_units) {
    return (
      <UnitSelection
        availableUnits={sessionResponse.available_units}
        onUnitSelected={handleUnitSelected}
        onError={handleUnitSelectionError}
      />
    );
  }

  return null;
};

export default Callback;

